import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Crown, Coins, Save, X, Tag, ListChecks } from "lucide-react";
import api, { apiErr } from "../lib/api";
import Btn from "../components/Btn";

const fmt = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

const EMPTY = {
  product_id: "", name: "", amount: 0, original_amount: 0, currency: "idr", type: "subscription",
  grant: { vip_days: 0, tokens: 0, energy: 0 }, features: [], discount_label: "", promo_ends_at: "", tryout_slots: 0, base_package_id: "", order: 100, active: true,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product object or "new"
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/products").then(({ data }) => setProducts(data.products)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Hapus produk "${p.name}"?`)) return;
    try { await api.delete(`/admin/products/${p.product_id}`); toast.success("Produk dihapus"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div className="min-h-[30vh] grid place-items-center"><div className="w-10 h-10 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>;

  if (editing) return <ProductForm initial={editing === "new" ? EMPTY : editing} isNew={editing === "new"} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  const subs = products.filter((p) => p.type === "subscription");
  const tokens = products.filter((p) => p.type === "tokens");
  const packs = products.filter((p) => p.type === "tryout_pack");

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-black text-lg">Kelola Produk VIP, Token & Paket Tryout</h2>
          <p className="text-su-muted font-medium text-sm">Tambah, edit, atau hapus langganan, paket tryout, diskon & fitur.</p>
        </div>
        <Btn data-testid="product-new" onClick={() => setEditing("new")}><Plus className="w-4 h-4" /> Tambah Produk</Btn>
      </div>

      <Section title="Paket Tryout" icon={ListChecks} items={packs} onEdit={setEditing} onRemove={remove} />
      <Section title="Langganan VIP" icon={Crown} items={subs} onEdit={setEditing} onRemove={remove} />
      <Section title="Paket Token" icon={Coins} items={tokens} onEdit={setEditing} onRemove={remove} />
    </div>
  );
}

function Section({ title, icon: Icon, items, onEdit, onRemove }) {
  return (
    <div>
      <h3 className="font-heading font-black text-base mb-3 flex items-center gap-2"><Icon className="w-5 h-5 text-su-secondary" /> {title}</h3>
      {items.length === 0 ? <p className="text-su-muted text-sm">Belum ada produk.</p> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((p) => (
            <div key={p.product_id} className="card-chunky p-5" data-testid={`product-card-${p.product_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-heading font-black">{p.name}</h4>
                    {p.discount_label && <span className="inline-flex items-center gap-1 text-[10px] font-heading font-black bg-su-secondary/20 text-su-secondaryShadow px-2 py-0.5 rounded-full"><Tag className="w-3 h-3" />{p.discount_label}</span>}
                    {!p.active && <span className="text-[10px] font-heading font-black bg-su-track text-su-muted px-2 py-0.5 rounded-full">NONAKTIF</span>}
                  </div>
                  <p className="font-heading font-black text-xl text-su-primary mt-1 flex items-center gap-2">{p.original_amount > p.amount && <span className="text-sm font-bold text-su-muted line-through">{fmt(p.original_amount)}</span>}{fmt(p.amount)}</p>
                  <p className="text-xs text-su-muted mt-1">
                    {p.grant?.vip_days ? `${p.grant.vip_days} hari VIP · ` : ""}
                    {p.grant?.tokens ? `${p.grant.tokens} token · ` : ""}
                    {p.grant?.energy ? `${p.grant.energy} energi` : ""}
                  </p>
                  {p.features?.length > 0 && <p className="text-xs text-su-muted mt-1">{p.features.length} fitur</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`product-edit-${p.product_id}`} onClick={() => onEdit(p)} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border hover:border-su-primary transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`product-delete-${p.product_id}`} onClick={() => onRemove(p)} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border hover:border-su-danger text-su-danger transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductForm({ initial, isNew, onDone, onCancel }) {
  const initOrig = Number(initial.original_amount) > 0 ? Number(initial.original_amount) : Number(initial.amount) || 0;
  const initDisc = initOrig > 0 && Number(initial.amount) > 0 ? Math.max(0, Math.round((1 - Number(initial.amount) / initOrig) * 100)) : 0;
  const [f, setF] = useState(() => ({ ...initial, original_amount: initOrig, promo_ends_at: toLocalInput(initial.promo_ends_at), grant: { vip_days: 0, tokens: 0, energy: 0, ...initial.grant }, features: [...(initial.features || [])] }));
  const [disc, setDisc] = useState(initDisc);
  const [busy, setBusy] = useState(false);
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => { api.get("/packages").then(({ data }) => setPkgs(data.packages || [])).catch(() => {}); }, []);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setGrant = (k, v) => setF((s) => ({ ...s, grant: { ...s.grant, [k]: Number(v) || 0 } }));

  const recalc = (orig, d) => {
    orig = Number(orig) || 0;
    d = Math.min(100, Math.max(0, Number(d) || 0));
    const final = Math.round(orig * (1 - d / 100));
    setF((s) => ({ ...s, original_amount: orig, amount: final, discount_label: d > 0 ? `HEMAT ${d}%` : "" }));
  };
  const onOrig = (v) => { set("original_amount", v); recalc(v, disc); };
  const onDisc = (v) => { const d = Math.min(100, Math.max(0, Number(v) || 0)); setDisc(d); recalc(f.original_amount, d); };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Nama produk wajib diisi"); return; }
    setBusy(true);
    const payload = {
      ...f, amount: Number(f.amount) || 0, original_amount: Number(f.original_amount) || 0, order: Number(f.order) || 100,
      promo_ends_at: f.promo_ends_at ? new Date(f.promo_ends_at).toISOString() : "",
      tryout_slots: Number(f.tryout_slots) || 0, base_package_id: f.base_package_id || "",
      features: f.features.map((x) => x.trim()).filter(Boolean),
      grant: { vip_days: Number(f.grant.vip_days) || 0, tokens: Number(f.grant.tokens) || 0, energy: Number(f.grant.energy) || 0 },
    };
    try {
      if (isNew) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${initial.product_id}`, payload);
      toast.success(isNew ? "Produk ditambahkan!" : "Produk diperbarui!");
      onDone();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const fmtRp = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
  const inputCls = "w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-primary outline-none transition-colors";
  const lbl = "text-xs font-heading font-black uppercase text-su-muted mb-1.5 block";

  return (
    <div className="card-chunky p-6 max-w-2xl" data-testid="product-form">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-black text-lg">{isNew ? "Tambah Produk" : "Edit Produk"}</h2>
        <button onClick={onCancel} data-testid="product-cancel" className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-4">
        <div>
          <label className={lbl}>Nama Produk</label>
          <input data-testid="pf-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="SkorUp VIP · Bulanan" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Tipe</label>
            <select data-testid="pf-type" value={f.type} onChange={(e) => set("type", e.target.value)} className={inputCls}>
              <option value="subscription">Langganan VIP</option>
              <option value="tryout_pack">Paket Tryout</option>
              <option value="tokens">Paket Token</option>
            </select>
          </div>
          <div>
            <label className={lbl}>Urutan</label>
            <input data-testid="pf-order" type="number" value={f.order} onChange={(e) => set("order", e.target.value)} className={inputCls} />
          </div>
        </div>
        {f.type === "tryout_pack" && (
          <div className="grid grid-cols-2 gap-4 rounded-2xl border-2 border-su-primary/30 bg-su-primary/5 p-4" data-testid="pf-tryout-box">
            <div>
              <label className={lbl}>Jumlah Tryout (slot)</label>
              <input data-testid="pf-tryout-slots" type="number" min="1" value={f.tryout_slots} onChange={(e) => set("tryout_slots", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={lbl}>Basis Ujian / Paket</label>
              <select data-testid="pf-base-package" value={f.base_package_id} onChange={(e) => set("base_package_id", e.target.value)} className={inputCls}>
                <option value="">— Pilih paket —</option>
                {pkgs.map((pk) => <option key={pk.package_id} value={pk.package_id}>{pk.title} ({pk.exam})</option>)}
              </select>
            </div>
            <p className="col-span-2 text-[11px] text-su-muted">Siswa dapat mengerjakan sebanyak "Jumlah Tryout" ini TANPA token/energi. Tiap tryout punya papan peringkat sendiri.</p>
          </div>
        )}
        <div className="rounded-2xl border-2 border-su-border bg-su-bg/50 p-4 space-y-3" data-testid="pf-pricing-box">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Harga Awal (Rp)</label>
              <input data-testid="pf-original-amount" type="number" value={f.original_amount} onChange={(e) => onOrig(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={lbl}>Diskon (%)</label>
              <input data-testid="pf-discount-percent" type="number" min="0" max="100" value={disc} onChange={(e) => onDisc(e.target.value)} placeholder="0" className={inputCls} />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-su-surface border-2 border-su-primary/30 px-4 py-3">
            <span className="text-xs font-heading font-black uppercase text-su-muted">Harga Akhir</span>
            <span className="flex items-center gap-2" data-testid="pf-final-amount">
              {disc > 0 && <span className="text-sm font-bold text-su-muted line-through">{fmtRp(f.original_amount)}</span>}
              <span className="font-heading font-black text-xl text-su-primary">{fmtRp(f.amount)}</span>
              {disc > 0 && <span className="text-[10px] font-heading font-black bg-su-secondary/20 text-su-secondaryShadow px-2 py-0.5 rounded-full">-{disc}%</span>}
            </span>
          </div>
          <p className="text-[11px] text-su-muted">Isi Harga Awal lalu Diskon (%) — Harga Akhir otomatis dihitung & inilah yang ditagihkan ke siswa.</p>
          <div>
            <label className={lbl}>Promo Berakhir (opsional)</label>
            <input data-testid="pf-promo-ends" type="datetime-local" value={f.promo_ends_at} onChange={(e) => set("promo_ends_at", e.target.value)} className={inputCls} />
            <p className="text-[11px] text-su-muted mt-1">Jika diisi, kartu VIP menampilkan hitung mundur "Diskon berakhir dalam...". Kosongkan bila tanpa batas.</p>
          </div>
        </div>
        <div>
          <label className={lbl}>Manfaat yang Diberikan</label>
          <div className="grid grid-cols-3 gap-3">
            <div><span className="text-[11px] text-su-muted font-bold">Hari VIP</span><input data-testid="pf-vipdays" type="number" value={f.grant.vip_days} onChange={(e) => setGrant("vip_days", e.target.value)} className={inputCls} /></div>
            <div><span className="text-[11px] text-su-muted font-bold">Token</span><input data-testid="pf-tokens" type="number" value={f.grant.tokens} onChange={(e) => setGrant("tokens", e.target.value)} className={inputCls} /></div>
            <div><span className="text-[11px] text-su-muted font-bold">Energi</span><input data-testid="pf-energy" type="number" value={f.grant.energy} onChange={(e) => setGrant("energy", e.target.value)} className={inputCls} /></div>
          </div>
        </div>
        <div>
          <label className={lbl}>Daftar Fitur (ditampilkan di kartu harga)</label>
          <div className="space-y-2">
            {f.features.map((feat, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid={`pf-feature-${i}`} value={feat} onChange={(e) => setF((s) => { const a = [...s.features]; a[i] = e.target.value; return { ...s, features: a }; })} className={inputCls} />
                <button onClick={() => setF((s) => ({ ...s, features: s.features.filter((_, j) => j !== i) }))} className="w-11 shrink-0 grid place-items-center rounded-xl border-2 border-su-border text-su-danger"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Btn variant="ghost" size="sm" data-testid="pf-add-feature" onClick={() => setF((s) => ({ ...s, features: [...s.features, ""] }))}><Plus className="w-4 h-4" /> Tambah Fitur</Btn>
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="pf-active" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 accent-[var(--su-primary)]" />
          <span className="font-heading font-bold text-sm">Aktif (tampil di halaman harga)</span>
        </label>
      </div>
      <div className="flex gap-3 mt-6">
        <Btn data-testid="pf-save" onClick={save} disabled={busy} className="flex-1"><Save className="w-4 h-4" /> {busy ? "Menyimpan..." : "Simpan"}</Btn>
        <Btn variant="ghost" onClick={onCancel}>Batal</Btn>
      </div>
    </div>
  );
}
